import React, { useEffect, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { num1 } from '../../lib/numeros';
import { pasoDelCampo } from '../../lib/pasoDeCantidad';

/**
 * «EXTRAS DEL DÍA» (apartado 5 del doc del 21-08; rehecho con los puntos 27 a 31 del doc
 * del 24-08).
 *
 * Lo que el cliente se come FUERA de su dieta -- el cumpleaños, el picoteo -- no tenía
 * dónde apuntarse: o lo metía en una comida (y descuadraba el reparto) o no lo contaba
 * (y la app creía que iba perfecto).
 *
 * UN CAMPO DE TEXTO Y YA. Hasta el 24-08 esto eran cuatro pasos: botón, ventana, buscar en
 * el catálogo y gramos. Un extra es por definición lo que NO estaba en la dieta -- el
 * pincho del bar, la tarta del cumpleaños --, justo lo que no va a encontrar en el
 * catálogo, y si no lo encuentra no lo apunta. El dato lo confirmó: con esto vivo desde el
 * 21-08 y 198 clientes, en toda la base de producción había UN solo extra apuntado. Ahora
 * escribe «Dos cañas y un pincho de tortilla» y sigue con su día.
 *
 * LO QUE ENTRA POR EL BUSCADOR RESTA; LO ESCRITO A MANO SOLO QUEDA APUNTADO (regla del 4-09,
 * ficha 03 del documento del 5-09). ANULA el punto 28 del 24-08, que los dejaba a todos fuera
 * de «Llevas» para que un extra no encogiera el «Falta». El motivo de aquello era bueno
 * -- comerse una tarta y que la app te diga que te saltes la comida 4 es enseñar a compensar --
 * pero se pesó contra el otro fallo: apuntar 80 g de hidratos de más y que «Falta» siguiera
 * pidiendo 120 es medir contra un número que ya no es verdad. Ganó decir la verdad. Un extra
 * del catálogo trae sus macros, suma en «Llevas» y encoge «Falta»; uno escrito a mano no
 * tiene ficha de la que sacar macros y por eso solo queda anotado. Cada línea de la lista lo
 * dice con su etiqueta («RESTA» / «SOLO APUNTADO») para que no haya que adivinarlo. La suma
 * la hace el padre (`TuDietaHoy`, `llevasDeLosExtras`); aquí se dice la regla y se pinta.
 *
 * El dato vive en el servidor (`extras` en el documento del día): POST /diets/{fecha}/extras
 * con `{texto}`, DELETE para quitarlo. El estado lo lleva el padre (TuDietaHoy o
 * NutritionPage), que avisa con `onAnadido` / `onQuitado`; aquí no se guarda copia propia.
 * Los extras viejos, los del catálogo, siguen llegando con macros y se pintan como antes.
 *
 * `origen` dice por dónde entró el extra (`inicio`, `nutricion` o `checkin`) y lo guarda el
 * servidor. Lo pone EL PADRE y no tiene valor por defecto a propósito: este bloque se monta
 * en cuatro sitios -- el Inicio, Nutrición, el panel del día y la pregunta de la noche del
 * check-in --, y si se dejara fijo aquí lo apuntado desde Nutrición se contaría como del
 * Inicio. Un origen que no consta se arregla mirando; uno falseado no se nota nunca.
 */

// Lo que cabe en un extra. El servidor corta en el mismo número (`_LARGO_MAX_EXTRA`), y
// aquí se avisa antes de que el campo deje de responder sin decir nada.
const LARGO_MAX = 300;

// «22P · 0H · 2G», los tres SIEMPRE y con la letra detrás del número, como los dibuja él en
// la maqueta de la ficha 03. El cero se escribe: en una línea de tres macros, el hueco de uno
// que falta se lee como un dato que no se sabe, y aquí se sabe y vale cero.
const lineaMacros = (m) => ['P', 'H', 'G'].map((k) => `${num1(m?.[k] || 0)}${k}`).join(' · ');

// De dónde vino el extra, que es lo que decide si resta. Del catálogo si trae ficha
// (`alimento_id`) o macros; a mano si no. Se mira `alimento_id` y no solo los macros porque
// un alimento del catálogo puede tener los tres a cero (una infusión) y sigue siendo del
// catálogo: lo que cambia la regla es de dónde salió, no cuánto suma.
const delCatalogo = (e) => Boolean(e?.alimento_id != null || e?.macros);

// La cantidad, pegada al nombre: «Pechuga de pollo · 120 g» (maqueta). `cantidad_texto` ya
// viene del servidor con las unidades bien puestas («2 ud (126 g)»).
const cantidadDelExtra = (e) => e.cantidad_texto
    || (e.cantidad_g ? `${num1(e.cantidad_g)} g` : null);

/**
 * `plegable`: EL «+», EN NUTRICIÓN Y EN INICIO (Jesús, 6-09 y 9-09-2026).
 *
 * Su frase: «Cuando abro Nutrición, aquí quiero que extras salga con el más, no abierto.
 * Abierto solo si ya tiene algo». Así que el bloque nace cerrado detrás de un «+» y se abre
 * solo cuando ya hay algo apuntado ese día, que es cuando hay lista que enseñar. Donde el
 * bloque enseña su propio rótulo -- Nutrición e Inicio -- va plegado; en la pregunta de la
 * noche del check-in NO, porque allí va `sinRotulo` y la puerta es la propia pregunta:
 * plegarlo sería pedirle dos veces que abra lo mismo.
 *
 * Es el tercer viaje de esta decisión, y conviene tener la serie entera a la vista para no
 * volver a darla la vuelta sin querer:
 *
 *     abierto en todas partes  ->  hasta el 2-09
 *     cerrado en las tres      ->  Francisco, 2-09 («tiene que ser un botón de más y ahí
 *                                  mostrar el buscador»), y `desplegado` era SOLO `abierto`
 *     abierto en las cuatro    ->  Francisco, 5-09, con la ficha 03 del documento de Jesús
 *     cerrado solo en Nutrición -> Jesús, 6-09
 *     y también en Inicio       -> Jesús, 9-09 («a extras del día le falta el signo más, y
 *                                  al desplegar recién mostrar sus campos»)  <- manda esto
 *
 * Y con una diferencia respecto al 2-09 que es justo lo que él pide: `desplegado` vuelve a
 * ser `abierto || hay extras`. Aquel día se quitó ese segundo trozo a propósito -- «con algo
 * apuntado se abría entero otra vez y volvía el formulario a la pantalla» --, pero es
 * literalmente lo que dice su frase, y ahora la lista de lo apuntado va DENTRO de lo que se
 * pliega: con el bloque cerrado y algo apuntado, el registro no se vería.
 *
 * LO QUE SIGUE FUERA son los cuatro pasos del 24-08: nada de ventana, ni de tener que abrir
 * nada para escribir. Lo del catálogo sí pregunta cuánto ha sido -- sin cantidad no hay
 * macros que restar --, pero lo hace en la misma tarjeta y solo después de elegir un
 * alimento; el buscador y el campo de escribir no dependen de ningún clic previo.
 */
//: Los que se cuentan por piezas: ahí la cantidad son unidades y no gramos.
const esPorUnidad = (food) => Boolean(food?.por_unidad ?? food?.unidades);

/**
 * `sinRotulo`: el bloque sin su título ni su frase, solo la lista y los campos.
 *
 * Es para la pregunta de la noche del check-in (punto 32), donde el rótulo ya lo pone la
 * propia pregunta: «¿Se te ha escapado algo más hoy? · Si no lo pusiste en el apartado de
 * extras, ponlo ahora». Repetir debajo «Extras del día · Si comes algo que no estaba previsto
 * en tu dieta, ponlo aquí EN CUANTO PASE» sería decir dos veces lo mismo y, de noche y
 * cerrando el día, dar un consejo que ya no toca. En Inicio, Nutrición y el panel del día el
 * rótulo se queda: allí es lo que dice para qué sirve el bloque.
 */
const ExtrasDelDia = ({ api, fecha, extras = [], onAnadido, onQuitado, origen,
                        sinRotulo = false, plegable = false, compacto = false }) => {
    const [texto, setTexto] = useState('');
    // Cerrado de partida donde se pliega. Lo abre el «+», y no se vuelve a cerrar solo:
    // quien acaba de apuntar algo suele apuntar lo siguiente detrás.
    const [abierto, setAbierto] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [quitando, setQuitando] = useState(null);
    // ── El buscador, que vuelve (punto 1.2 del documento del 1-09) ───────────
    const [query, setQuery] = useState('');
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [elegido, setElegido] = useState(null);
    const [cantidad, setCantidad] = useState('');

    // Con su respiro de 300 ms, como el resto de buscadores de la casa.
    useEffect(() => {
        if (elegido) return undefined;
        const q = query.trim();
        if (q.length < 2) { setResultados([]); setBuscando(false); return undefined; }
        setBuscando(true);
        const t = setTimeout(() => {
            api.get('/calculator/search', { params: { q, limit: 30 } })
                .then((r) => { setResultados(r.data?.alimentos || []); setBuscando(false); })
                .catch((err) => {
                    console.error('[extras] no se pudo buscar en el catálogo', err);
                    setResultados([]); setBuscando(false);
                });
        }, 300);
        return () => clearTimeout(t);
    }, [query, elegido, api]);

    const soltarLaBusqueda = () => {
        setQuery(''); setResultados([]); setElegido(null); setCantidad('');
    };

    const elegir = (food) => {
        setElegido(food);
        // Un arranque razonable que el cliente corrige: 1 unidad, o 100 g.
        setCantidad(esPorUnidad(food) ? '1' : '100');
    };

    const apuntarDelCatalogo = async () => {
        const valor = parseFloat(String(cantidad).replace(',', '.'));
        if (!valor || valor <= 0) { toast.error('Dime cuánto ha sido.'); return; }
        if (guardando) return;
        setGuardando(true);
        try {
            const cuerpo = esPorUnidad(elegido)
                ? { alimento_id: elegido.id, unidades: valor }
                : { alimento_id: elegido.id, cantidad_g: valor };
            const r = await api.post(`/diets/${fecha}/extras`,
                origen ? { ...cuerpo, origen } : cuerpo);
            onAnadido?.(r.data.extra);
            soltarLaBusqueda();
        } catch (err) {
            console.error('[extras] no se pudo apuntar el extra', err);
            toast.error('No se pudo apuntar el extra. Prueba otra vez.');
        } finally {
            setGuardando(false);
        }
    };

    const apuntar = async () => {
        const limpio = texto.trim();
        if (!limpio || guardando) return;
        setGuardando(true);
        try {
            // Sin `origen` no se manda el campo: el servidor lo guarda vacío y se ve que
            // ese sitio todavía no lo dice, en vez de apuntarlo donde no fue.
            const r = await api.post(`/diets/${fecha}/extras`,
                origen ? { texto: limpio, origen } : { texto: limpio });
            onAnadido?.(r.data.extra);
            setTexto('');
        } catch (err) {
            console.error('[extras] no se pudo apuntar el extra', err);
            toast.error('No se pudo apuntar el extra. Prueba otra vez.');
        } finally {
            setGuardando(false);
        }
    };

    const quitar = async (extra) => {
        setQuitando(extra.id);
        try {
            await api.delete(`/diets/${fecha}/extras/${extra.id}`);
            onQuitado?.(extra.id);
        } catch (err) {
            console.error('[extras] no se pudo quitar el extra', err);
            toast.error('No se pudo quitar el extra. Prueba otra vez.');
        } finally {
            setQuitando(null);
        }
    };

    // LO QUE LLEVA EN EXTRAS, SUMADO EN SU LÍNEA.
    //
    // Un extra buscado en el catálogo trae sus macros; uno escrito a mano no tiene de dónde
    // sacarlos. Aquí se suman los que los tienen, y se enseñan en su propia línea.
    //
    // Y AHORA ESA SUMA YA ESTÁ DENTRO DE «LLEVAS» (5-09). Hasta el 5-09 esta línea decía
    // «Además de tu dieta, hoy llevas ... en extras», porque los extras se contaban aparte y
    // no movían el «Falta» (punto 28 del 24-08). La regla del 4-09 lo cambió: lo del catálogo
    // resta. La línea tenía que cambiar con ella o se quedaba diciendo lo contrario de lo que
    // hace la app -- «además» se lee como «esto va por fuera de tus números» --, así que
    // ahora dice que ya está restado.
    const enExtras = extras.reduce((acc, e) => ({
        P: acc.P + (e.macros?.P || 0),
        H: acc.H + (e.macros?.H || 0),
        G: acc.G + (e.macros?.G || 0),
    }), { P: 0, H: 0, G: 0 });
    const hayQueRestar = enExtras.P + enExtras.H + enExtras.G > 0;

    // «Abierto solo si ya tiene algo» (Jesús, 6-09), y abierto siempre donde no se pliega.
    const desplegado = !plegable || abierto || extras.length > 0;

    // «QUE ABULTE MENOS» (Jesús, vídeo del 26-09, en su maqueta de Inicio): cerrado, el bloque
    // es solo un botón pequeño, «+ Apuntar un extra», debajo de las comidas. Al pulsarlo se
    // abre lo de siempre, con su rótulo y su explicación.
    if (compacto && plegable && !desplegado) {
        return (
            <section data-testid="extras-del-dia">
                <button type="button" onClick={() => setAbierto(true)} data-testid="extras-abrir"
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border border-brand/70 text-brand text-sm font-semibold hover:bg-brand/10 transition-colors">
                    <span className="text-base leading-none">+</span> Apuntar un extra
                </button>
            </section>
        );
    }

    return (
        <section className="space-y-3" data-testid="extras-del-dia">
            {/* El texto de Jesús, literal (punto 30). Se ve con la lista llena y con la lista
                vacía: es el rótulo del bloque, y con lista llena es cuando más falta hace
                que se entienda que esto es para lo de fuera de la dieta. Solo se calla donde
                el rótulo ya lo pone otro (`sinRotulo`, la pregunta de la noche).
                «PREVISTO» (punto 175 del 27-08): decía «algo que no estaba en tu dieta», que
                se lee como una falta. Previsto quita la culpa -- no está prohibido, es que no
                entraba en el plan de hoy -- y así lo apunta en vez de callárselo.

                Este comentario llevaba aquí desde el 27-08 describiendo un cambio que NUNCA
                se hizo: la frase de abajo seguía siendo la vieja. Corregido el 2-09 con el
                texto entero que él escribió en el punto 175, que también cambia el final
                («en cuanto pase» en vez de «por pequeño que sea»). */}
            {(!sinRotulo || (plegable && !desplegado)) && (
                <div className="flex items-start justify-between gap-3">
                    {!sinRotulo && (
                        <div className="min-w-0">
                            <p className="caption">Extras del día</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Si comes algo que no estaba previsto en tu dieta, ponlo aquí en cuanto pase.
                            </p>
                        </div>
                    )}
                    {/* EL «+» (Jesús, 6-09). El rótulo se queda a la vista con el bloque
                        cerrado: es lo que dice para qué sirve esto, y sin él el «+» no se
                        entiende. Lo que se esconde es el formulario. */}
                    {plegable && !desplegado && (
                        <button onClick={() => setAbierto(true)} data-testid="extras-abrir"
                            aria-label="Apuntar un extra del día"
                            // EN NARANJA, COMO EN INICIO (Jesús, 27-09: «el botón de añadir extra no
                            // sale en naranja si lo ves dentro de la pestaña de nutrición»).
                            className="flex-shrink-0 w-9 h-9 rounded-full bg-brand text-white text-xl font-bold leading-none flex items-center justify-center hover:opacity-90 transition-opacity">
                            +
                        </button>
                    )}
                    {/* Y EL BOTÓN DE VOLVER A CERRARLO (Francisco, 13-09): «no veo el botón
                        para ocultar los extras». El «+» abría y no había vuelta: el
                        formulario se quedaba puesto el resto de la sesión, que es justo lo
                        que el «+» del 9-09 venía a evitar. Es la misma pastilla, con el
                        signo al revés.
                        Solo cuando NO hay nada apuntado: con la lista llena, plegar
                        escondería el registro del día, y esa es la razón por la que
                        `desplegado` cuenta los extras. */}
                    {plegable && desplegado && abierto && extras.length === 0 && (
                        <button onClick={() => setAbierto(false)} data-testid="extras-cerrar"
                            aria-label="Ocultar los extras del día"
                            className="flex-shrink-0 w-9 h-9 rounded-full border border-border text-muted-foreground text-xl font-bold leading-none flex items-center justify-center hover:border-brand/50 hover:text-brand transition-colors">
                            −
                        </button>
                    )}
                </div>
            )}

            {/* LO APUNTADO, ENCIMA DEL CAMPO (punto 29), Y CADA LÍNEA DICE SI RESTA (ficha 03).
                La regla no se explica en un párrafo aparte: va pegada a la línea que la
                cumple, que es donde se lee sin buscarla. Verde «RESTA» para lo que entró por
                el buscador -- trae macros, suma en «Llevas» y encoge «Falta» -- y gris «SOLO
                APUNTADO» para lo escrito a mano, que se queda de registro. */}
            {desplegado && extras.map((e) => {
                const resta = delCatalogo(e);
                const cantidad = cantidadDelExtra(e);
                return (
                    <div key={e.id} data-testid={`extra-${e.id}`}
                        className="surface p-3.5 sm:p-4 flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                            <p className="font-bold text-sm text-foreground">
                                {e.texto || e.nombre}{cantidad ? ` · ${cantidad}` : ''}
                            </p>
                            {/* Debajo, lo que aporta y la etiqueta, en la misma línea: los
                                macros si los tiene y «a ojo» si se escribió a mano, como en la
                                maqueta. La etiqueta va AQUÍ y no en su propia columna a la
                                derecha porque en un móvil de 390 px «SOLO APUNTADO» le comía
                                media línea al nombre y «Dos cañas y un pincho de tortilla»
                                salía partido en dos. */}
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm text-muted-foreground font-data">
                                    {resta ? lineaMacros(e.macros) : 'a ojo'}
                                </p>
                                <span data-testid={`extra-etiqueta-${e.id}`}
                                    title={resta
                                        ? 'Lo buscaste en el catálogo: sus macros ya te restan de lo que te queda hoy.'
                                        : 'Escrito a mano: no tiene macros, así que queda apuntado y no te resta.'}
                                    className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                        resta
                                            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                                            : 'bg-muted text-muted-foreground'}`}>
                                    {resta ? 'Resta' : 'Solo apuntado'}
                                </span>
                            </div>
                        </div>
                        <button onClick={() => quitar(e)} disabled={quitando === e.id}
                            aria-label={`Quitar ${e.texto || e.nombre} de los extras`}
                            data-testid={`quitar-extra-${e.id}`}
                            className="flex-shrink-0 p-2 -m-1 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-40">
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </div>
                );
            })}

            {/* La suma de lo buscado, en su propia línea. Sale solo si hay algo que restar:
                los extras escritos a mano no traen macros, y una línea a cero no informa de
                nada. */}
            {desplegado && hayQueRestar && (
                <p className="text-xs text-muted-foreground" data-testid="extras-suma">
                    Lo que buscaste ya te resta <span className="font-data text-foreground">{lineaMacros(enExtras)}</span> de lo que te queda hoy.
                </p>
            )}

            {/* `hidden` y no desmontar: lo que se lleve escrito a medias y lo que esté
                buscando siguen ahí si lo cierra y lo vuelve a abrir. */}
            <div className={`space-y-2 ${desplegado ? '' : 'hidden'}`}>
                {/* EL BUSCADOR DELANTE, LA CAJA DETRÁS (punto 1.2 del documento del 1-09).
                    El 24-08 se quitó el catálogo de aquí y quedó solo el texto libre, con un
                    motivo bueno: eran cuatro pasos para apuntar un pincho de tortilla, y en
                    toda la base había UN extra apuntado. Pero el texto libre no cuenta macros
                    -- no hay ficha de la que sacarlos --, así que quien se come algo que SÍ
                    está en el catálogo no tenía forma de que contara.
                    Vuelven los dos, en el orden del documento: primero buscarlo, y si no está,
                    escribirlo. Lo que hace cada uno lo dice la etiqueta de la línea que deja
                    apuntada, arriba: «RESTA» o «SOLO APUNTADO». */}
                {!elegido && (
                    <div className="relative">
                        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                        {/* LA LETRA, DEL TAMAÑO DE LA TARJETA (Jesús, 6-09: «el texto de los
                            extras sale muy grande, tanto en la pantalla del check-in diario
                            como en la de inicio»). `Input` y `Textarea` de la casa vienen con
                            `text-base` y solo bajan a `text-sm` de tablet en adelante, así que
                            en el móvil -- que es donde él lo vio -- estos dos campos salían a
                            16 px dentro de un bloque escrito entero a 14. Se baja aquí y no en
                            `components/ui`, que es de toda la app.
                            LO QUE CUESTA, para quien lo lea luego: en Safari de iPhone un campo
                            por debajo de 16 px hace que la página dé un salto de zoom al
                            tocarlo. Pasa ya en el buscador de alimentos de la dieta, que lleva
                            `text-sm` desde siempre. */}
                        <Input value={query} onChange={(ev) => setQuery(ev.target.value)}
                            placeholder="Buscar el alimento…"
                            aria-label="Buscar el alimento en el catálogo"
                            className="pl-10 rounded-xl bg-muted border-0 text-sm"
                            data-testid="extras-buscar" />
                    </div>
                )}

                {/* Los resultados, cortos: es una lista para reconocer lo que se ha comido,
                    no para elegir el mejor encaje de macros (eso es el buscador de la dieta). */}
                {!elegido && query.trim().length >= 2 && (
                    <div className="max-h-56 overflow-y-auto space-y-1" data-testid="extras-resultados">
                        {buscando && <p className="text-xs text-muted-foreground px-1">Buscando…</p>}
                        {!buscando && resultados.length === 0 && (
                            <p className="text-xs text-muted-foreground px-1">
                                Eso no está en el catálogo. Escríbelo aquí abajo.
                            </p>
                        )}
                        {resultados.map((food) => (
                            <button key={food.id} onClick={() => elegir(food)}
                                data-testid={`extras-elegir-${food.id}`}
                                className="w-full text-left rounded-lg border border-border px-3 py-2 hover:border-brand/50 transition-colors">
                                <span className="block text-sm text-foreground">{food.nombre}</span>
                                <span className="block text-xs text-muted-foreground">
                                    por {esPorUnidad(food) ? 'unidad' : '100 g'}
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Elegido uno, lo único que falta es cuánto. */}
                {elegido && (
                    <div className="surface p-3 space-y-2" data-testid="extras-cuanto">
                        <p className="text-sm font-bold text-foreground">{elegido.nombre}</p>
                        <div className="flex items-center gap-2">
                            <Input type="number" inputMode="decimal" min="0"
                                step={esPorUnidad(elegido) ? pasoDelCampo(elegido, true) : '5'}
                                value={cantidad} onChange={(ev) => setCantidad(ev.target.value)}
                                aria-label={esPorUnidad(elegido) ? 'Cuántas unidades' : 'Cuántos gramos'}
                                className="rounded-xl bg-muted border-0 text-sm"
                                data-testid="extras-cantidad" />
                            <span className="text-sm text-muted-foreground shrink-0">
                                {esPorUnidad(elegido) ? 'ud' : 'g'}
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={soltarLaBusqueda} data-testid="extras-otro"
                                className="flex-1 h-11 rounded-xl border border-border text-sm text-muted-foreground">
                                Buscar otro
                            </button>
                            <button onClick={apuntarDelCatalogo} disabled={guardando}
                                data-testid="extras-apuntar-catalogo"
                                className="flex-1 h-11 rounded-xl bg-brand text-white font-bold text-sm disabled:opacity-60">
                                {guardando ? 'Apuntando…' : 'Apuntarlo'}
                            </button>
                        </div>
                    </div>
                )}

                {/* FUERA «o si no está» (Francisco, 2-09), junto con el «a ojo» del campo y
                    la frase de qué cuenta y qué no. Eran tres explicaciones seguidas encima de
                    dos controles que se entienden solos: se busca, o se escribe. */}

                {/* LA CAJA DE ESCRIBIR A MANO VUELVE (Francisco, 5-09: «ese text area tiene que
                    volver»). El 2-09 salió entera («el textarea quítalo, no se va a usar») y
                    tres días después la pidió de vuelta: se restauró tal cual estaba, sin las
                    tres frases, que siguen fuera. Lo que se escribe aquí queda como registro
                    y no cuenta en los macros, igual que antes. */}
                {/* Tres filas porque en un móvil de 390 px el campo con dos se queda corto en
                    cuanto apunta la comida de un domingo entero. */}
                <Textarea rows={3} value={texto} maxLength={LARGO_MAX}
                    onChange={(ev) => setTexto(ev.target.value)}
                    /* Enter apunta; para partir la línea, Mayúsculas + Enter. Es una línea
                       de lo que se ha comido, no un texto largo. */
                    onKeyDown={(ev) => {
                        if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); apuntar(); }
                    }}
                    /* EL TEXTO EN GRIS DENTRO DEL CAMPO, literal de Jesús (puntos 29 y 31, y
                       la ficha 03 del 5-09). Es un placeholder y no una frase fija encima: se
                       va solo en cuanto escribe la primera letra, que es lo que él pidió, y
                       así no se queda ocupando sitio mientras apunta.
                       El 2-09 salió («Escríbelo a mano. En cuanto pase, con la cantidad a ojo
                       si no lo pesas»); el 5-09 vuelve con el texto nuevo de su documento. */
                    placeholder="Con la cantidad aproximada a ojo si no lo pesas, pero ponlo todo."
                    /* El campo no tiene etiqueta encima (el rótulo es el del bloque), así que
                       el nombre para quien lo lee con voz sale de aquí. */
                    aria-label="Apunta un extra del día"
                    /* MÁS PEQUEÑO QUE LA INSTRUCCIÓN DE ARRIBA (punto 176 del doc «Para el
                       equipo · parte 5»). Iban los dos al mismo tamaño y al mismo peso, así
                       que lo de dentro del campo pesaba igual que lo que le dice qué hacer,
                       y las dos frases competían. El marcador es una pista, no una orden.
                       Y `md:text-xs` NO SOBRA: el Textarea de la casa trae `md:text-sm` en
                       su clase base, así que con `text-xs` a secas el arreglo solo valía en
                       el móvil y en el ordenador los dos textos volvían al mismo tamaño.
                       Se vio midiendo a 1280, no a 390. */
                    className="rounded-xl bg-muted border-0 resize-none text-xs md:text-xs"
                    /* `extras-` y no `extra-`: cada fila apuntada es `extra-{id}`, así que
                       un guión que busque `[data-testid^="extra-"]` para contar lo apuntado
                       se llevaría también el campo y la cuenta. */
                    data-testid="extras-campo" />
                {/* La cuenta sale al acercarse al tope. Sin ella el campo se queda mudo de
                    golpe -- deja de aceptar letras y no dice por qué --, y quien está
                    poniendo la comida del domingo entero se cree que se ha colgado. Es un
                    número a secas y no una frase a propósito: los textos que lee el cliente
                    los escribe Jesús, y éste no está en su documento. */}
                {texto.length >= LARGO_MAX - 50 && (
                    <p className="text-xs text-muted-foreground text-right font-data"
                        data-testid="extras-cuenta">
                        {texto.length} / {LARGO_MAX}
                    </p>
                )}
                {/* El botón sale en cuanto escribe: con el campo vacío no pinta nada. */}
                {texto.trim() && (
                    <button onClick={apuntar} disabled={guardando} data-testid="apuntar-extra"
                        className="w-full h-12 rounded-xl bg-brand text-white font-bold text-sm disabled:opacity-60 transition-opacity">
                        {guardando ? 'Apuntando...' : 'Apuntarlo'}
                    </button>
                )}

                {/* Aquí iba «Lo que pongas a mano no cuenta en tus macros, simplemente queda
                    el registro. Lo que busques, sí» (punto 1.2 del 1-09). FUERA por decisión
                    suya del 2-09, y sigue fuera: lo que decía ahora lo dice cada línea con su
                    etiqueta («RESTA» / «SOLO APUNTADO»), que es donde hace falta y sin un
                    párrafo más encima del campo. */}
            </div>
        </section>
    );
};

export default ExtrasDelDia;
